/* eslint-disable no-param-reassign */
// Creates generic data model based off savant's default model
// Looks at the schema to determine what type of chart is being requested and creates the appropriate model
// This helps make the generic model simple for non savant/statsapi requests

// px required
// pz required
// legendType required

import get from 'lodash/get'

import {
    depthMidPlateInches,
    plateWidthFt,
    plateWidthInches,
} from 'bam-node-baseball-savant/constants/zone.ts'
import TooltipHtml from '../Tooltip/Html/SingleEvent.ts'

import {
    RemoveBadPitchPoints,
    RemoveBadSprayPoints,
} from '../Utilities/RemoveBadPoints.ts'
import {
    Challenge,
    ChartType,
    EVPData,
    GamefeedRawData,
    IllustratorRawData,
    isGamefeedRawData,
} from '../schema/types.ts'
import { SchemaConfig } from '../schema/default.ts'

export default function Convert(
    schema: SchemaConfig,
    savantData: IllustratorRawData[] | GamefeedRawData[]
) {
    let data: EVPData[] = []

    const chartOptions = get(schema, 'charts[0]')

    if (chartOptions.chartType) {
        if (
            chartOptions.chartType === 'pitch' ||
            chartOptions.chartType === 'release' ||
            chartOptions.chartType === 'field' ||
            chartOptions.chartType === 'radial' ||
            chartOptions.chartType === '3d' ||
            chartOptions.chartType === 'movement'
        ) {
            savantData = filterSavantData(chartOptions, savantData)
            data = savantData.map((d) =>
                formatSavantData(chartOptions, d, Boolean(schema.showToolTip))
            )
        } else {
            data = savantData.map((d) => formatSavantData(chartOptions, d))
        }
    }

    return data
}

// Remove various data points based on chart types
function filterSavantData(
    { chartType = 'pitch' }: { chartType: ChartType },
    savantData: GamefeedRawData[] | IllustratorRawData[]
) {
    if (
        chartType === 'pitch' ||
        chartType === 'movement' ||
        chartType === 'release' ||
        chartType === 'network'
    ) {
        return RemoveBadPitchPoints(savantData)
    }
    if (chartType === 'field' || chartType === 'radial' || chartType === '3d') {
        return RemoveBadSprayPoints(savantData)
    }

    return savantData
}

function formatAbsChallenge(pitch: IllustratorRawData): Challenge | undefined {
    return Number(pitch.is_abs_challenge)
        ? {
              is_batter:
                  Number(pitch.batting_team_id) ===
                  Number(pitch.abs_challenge_team_id),
              is_overturned: Boolean(Number(pitch.is_abs_overturned)),
              challenge_team_id: Number(pitch.abs_challenge_team_id),
          }
        : undefined
}

function formatSavantData(
    {
        legendType = 'pitchName',
        chartType = 'pitch',
    }: {
        legendType: keyof EVPData
        chartType: ChartType
    },
    d: GamefeedRawData | IllustratorRawData,
    showToolTip = false
): EVPData {
    const isGamefeed = isGamefeedRawData(d)

    if (isGamefeed) {
        return formatGamefeedData(chartType, legendType, d, showToolTip)
    }

    let x: keyof IllustratorRawData = 'plate_x'
    let y: keyof IllustratorRawData = 'plate_z'
    if (chartType === 'field' || chartType === '3d') {
        x = 'hc_x_ft'
        y = 'hc_y_ft'
    }
    if (chartType === 'pitch') {
        x = 'plate_x'
        y = 'plate_z'
    }
    if (chartType === 'movement') {
        x = 'pfx_x'
        y = 'pfx_z_gravity'
    }
    if (chartType === 'release') {
        x = 'x0'
        y = 'z0'
    }

    const hasStrikeZone = Boolean(d.sz_top && d.sz_bot)

    const legendKey = legendType as keyof IllustratorRawData
    const legend = d[legendKey]
    const abs_challenge = formatAbsChallenge(d)

    const isInZone = Number(d.zone) > 0 && Number(d.zone) < 10

    return {
        x: Number(d[x]),
        y: Number(d[y]),
        pitchType: d.pitch_type,
        pitchName: d.pitch_name,
        legend: legend ? String(legend) : null,
        paResult: d.events,
        pitchResult: d.description,
        hits: Number(d.is_hit_into_play_basehit),
        battedBallType: d.bb_type,
        ab: Number(d.is_ab),
        pa: Number(d.is_pa),
        bip: Number(d.is_hit_into_play),
        trackedBIP: Number(d.launch_speed) > 0 ? Number(d.is_hit_into_play) : 0,
        trackedSwingLength: Number(d.swing_length) ? 1 : 0,
        trackedBatSpeed: Number(d.sweetspot_speed_mph) ? 1 : 0,
        trackedSwingLengthCompetitive:
            Number(d.swing_length) && Number(d.is_competitive) ? 1 : 0,
        trackedBatSpeedCompetitive:
            Number(d.sweetspot_speed_mph) && Number(d.is_competitive) ? 1 : 0,
        single: Number(d.is_hit_into_play_basehit_single),
        double: Number(d.is_hit_into_play_basehit_double),
        triple: Number(d.is_hit_into_play_basehit_triple),
        hr: Number(d.is_hit_into_play_basehit_home_run),
        so: Number(d.is_strikeout),
        zone: d.zone,
        isInZone,
        balls: Number(d.pre_ball_count) || Number(d.balls),
        strikes: Number(d.pre_strike_count) || Number(d.strikes),
        releaseSpeed: Number(d.release_speed),
        releaseSpinRate: Number(d.release_spin_rate) || 0,
        pfxX: Number(d.pfx_x) || null,
        pfxZGravity: Number(d.pfx_z_gravity) || null,
        launchSpeed: d.is_hit_into_play ? Number(d.launch_speed) : 0, // TODO can we change these to undefined
        launchAngle: d.is_hit_into_play ? Number(d.launch_angle) : 0,
        swingLength: Number(d.swing_length) || null,
        batSpeed: Number(d.sweetspot_speed_mph) || null,
        is_swing: Number(d.is_swing) || null,
        is_whiff: Number(d.is_whiff) || null,
        deltaRunExp: Number(d.delta_run_exp),
        wobaNumer: Number(d.fangraphs_woba_numer),
        wobaDenom: Number(d.fangraphs_woba_denom),
        xWobaNumer: Number(d.fangraphs_est_woba_numer),
        xWobaDenom: Number(d.fangraphs_est_woba_denom),
        tooltip: showToolTip ? TooltipHtml(d, abs_challenge) : null,
        videoUrl:
            d.game_year >= 2016 && d.play_id
                ? `https://baseballsavant.mlb.com/sporty-videos?playId=${d.play_id}`
                : null,
        raw: d,
        hasStrikeZone,
        szBot: d.sz_bot ? Number(d.sz_bot) : null,
        szTop: d.sz_top ? Number(d.sz_top) : null,
        szDepth:
            !Number.isNaN(Number(d.sz_depth)) && d.sz_depth !== ''
                ? Number(d.sz_depth)
                : depthMidPlateInches,
        szWidth: plateWidthFt,
        is_abs_challenge: Boolean(Number(d.is_abs_challenge)),
        abs_challenge,
    }
}

function formatGamefeedData(
    chartType: ChartType,
    legendType: string,
    pitch: GamefeedRawData,
    showToolTip: boolean
): EVPData {
    // let x: keyof GamefeedRawData = 'plate_x'
    // let y: keyof GamefeedRawData = 'plate_z'

    let x: 'plate_x' | 'pfxX' | 'x0' | 'hc_x_ft' = 'plate_x'
    let y: 'plate_z' | 'pfxZWithGravity' | 'z0' | 'hc_y_ft' = 'plate_z'

    if (chartType === 'field' || chartType === '3d') {
        x = 'hc_x_ft'
        y = 'hc_y_ft'
    }

    if (chartType === 'pitch') {
        x = 'plate_x'
        y = 'plate_z'
    }
    if (chartType === 'movement') {
        x = 'pfxX'
        y = 'pfxZWithGravity'
    }
    if (chartType === 'release') {
        x = 'x0'
        y = 'z0'
    }

    const hasStrikeZone = Boolean(
        pitch.sz_top && pitch.sz_bot && pitch.sz_width
    )
    const isInZone = Number(pitch.zone) > 0 && Number(pitch.zone) < 10

    // const isPitch = pitch.type !== 'no_pitch'

    const legendKey = legendType as keyof GamefeedRawData
    const legend = pitch[legendKey]

    return {
        x: pitch[x] ?? null,
        y: pitch[y] ?? null,
        pitchType: pitch.pitch_type,
        pitchName: pitch.pitch_name,
        legend: legend ? String(legend) : null,
        paResult: pitch.events,
        pitchResult: pitch.description,
        trackedBatSpeed: Number(pitch.batSpeed) ? 1 : 0,
        zone: pitch.zone,
        isInZone,
        callName: pitch.call_name,
        balls: Number(pitch.balls),
        strikes: Number(pitch.strikes),
        releaseSpeed: Number(pitch.start_speed),
        releaseSpinRate: Number(pitch.spin_rate) || 0,
        pfxX: Number(pitch.pfxX * 12) || null,
        pfxZGravity: Number(Math.abs(pitch.pfxZWithGravity * 12)) || null,
        launchSpeed: Number(pitch.hit_speed) || 0,
        launchAngle: Number(pitch.hit_angle) || 0,
        batSpeed: Number(pitch.batSpeed) || null,
        tooltip: showToolTip ? TooltipHtml(pitch) : null,
        raw: pitch,
        hasStrikeZone,
        szBot: pitch.sz_bot && Number(pitch.sz_bot),
        szTop: pitch.sz_top && Number(pitch.sz_top),
        szDepth:
            (pitch.sz_depth && Number(pitch.sz_depth)) || depthMidPlateInches,
        szWidth: (pitch.sz_width || plateWidthInches) / 12,
        // ABS
        is_abs_challenge: pitch.is_abs_challenge,
        abs_challenge: pitch.abs_challenge,
    }
}
